import { useEffect, useRef, useState } from "react";
import { Mic, Square, Upload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const MAX_SECONDS = 30;

export default function VoiceRecorder({ onComplete, busy }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const canvasRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const silentFramesRef = useRef(0);
  const totalFramesRef = useRef(0);

  useEffect(() => () => cleanup(), []);

  const cleanup = () => {
    cancelAnimationFrame(rafRef.current);
    clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") audioCtxRef.current.close();
  };

  const draw = (analyser) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const buf = new Uint8Array(analyser.frequencyBinCount);
    const render = () => {
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += Math.abs(buf[i] - 128);
      const rms = sum / buf.length;
      totalFramesRef.current += 1;
      if (rms < 2) silentFramesRef.current += 1;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#D4AF37";
      ctx.beginPath();
      const slice = canvas.width / buf.length;
      let x = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = buf[i] / 128;
        const y = (v * canvas.height) / 2;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        x += slice;
      }
      ctx.stroke();
      rafRef.current = requestAnimationFrame(render);
    };
    render();
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      silentFramesRef.current = 0;
      totalFramesRef.current = 0;

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioCtx();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      draw(analyser);

      const recorder = new MediaRecorder(stream);
      mediaRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        const silenceRatio = totalFramesRef.current ? silentFramesRef.current / totalFramesRef.current : 1;
        cleanup();
        if (silenceRatio > 0.7) {
          toast.error("Too much silence detected. Hold a steady 'aaah' toward the mic and try again.");
          return;
        }
        onComplete(blob, "recording.webm");
      };
      recorder.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((e) => {
          if (e + 1 >= MAX_SECONDS) stop();
          return e + 1;
        });
      }, 1000);
    } catch {
      toast.error("Microphone access is required. Please allow it and try again.");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
    setRecording(false);
    clearInterval(timerRef.current);
    cancelAnimationFrame(rafRef.current);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onComplete(file, file.name);
  };

  return (
    <div className="flex flex-col items-center gap-8">
      <div className="relative grid place-items-center h-56 w-56">
        {recording && (
          <>
            <span className="absolute h-40 w-40 rounded-full bg-primary/20 pulse-ring" />
            <span className="absolute h-40 w-40 rounded-full bg-primary/10 pulse-ring" style={{ animationDelay: "0.8s" }} />
          </>
        )}
        <button
          onClick={recording ? stop : start}
          disabled={busy}
          data-testid="recorder-toggle-btn"
          className={`relative z-10 h-32 w-32 rounded-full grid place-items-center border transition-colors disabled:opacity-50 ${
            recording ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-primary border-border hover:border-primary"
          }`}
        >
          {busy ? <Loader2 size={40} className="animate-spin" strokeWidth={1.5} /> : recording ? <Square size={38} strokeWidth={1.5} /> : <Mic size={44} strokeWidth={1.5} />}
        </button>
      </div>

      <canvas ref={canvasRef} width={520} height={80} className={`w-full max-w-lg ${recording ? "opacity-100" : "opacity-0"} transition-opacity`} />

      <div className="text-center">
        <p className="font-serif text-2xl">{recording ? `${MAX_SECONDS - elapsed}s remaining` : "Ready to scan"}</p>
        <p className="text-sm text-muted-foreground mt-1">
          {recording ? "Hold a steady vowel tone toward the microphone." : "Tap the mic and hum or say 'aaah' for 30 seconds."}
        </p>
      </div>

      {!recording && !busy && (
        <label className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary cursor-pointer transition-colors" data-testid="recorder-upload-label">
          <Upload size={16} strokeWidth={1.5} /> or upload an audio file
          <input type="file" accept="audio/*" className="hidden" onChange={onFile} data-testid="recorder-file-input" />
        </label>
      )}
    </div>
  );
}
